<template>
  <div class="international-checkout-container">
    <div class="checkout-card">
      <div v-if="loading" class="checkout-loading-wrapper">
        <div class="spinner"></div>
        <p>{{ $t('InternationalCheckout.Loading') }}</p>
      </div>

      <div v-else-if="not_found" class="checkout-error-wrapper">
        <vs-alert color="danger" :title="$t('InternationalCheckout.AccessError')" active="true">
          <span>{{ $t('InternationalCheckout.AccessErrorDesc') }}</span>
        </vs-alert>
      </div>

      <div v-else-if="invoiceData" class="checkout-grid">
        <!-- Left Column: Registration & Billing Form -->
        <div class="checkout-form-section">
          <h2 class="section-title">{{ $t('InternationalCheckout.Title') }}</h2>
          <p class="section-subtitle">{{ $t('InternationalCheckout.Subtitle') }}</p>

          <ValidationObserver ref="checkoutForm" v-slot="{ invalid }">
            <form @submit.prevent="processCheckout">
              <!-- Contact Information -->
              <h3 class="form-group-title">{{ $t('InternationalCheckout.ContactInfoTitle') }}</h3>
              <div class="form-row">
                <div class="form-col">
                  <ValidationProvider :name="$t('InternationalCheckout.FirstName')" rules="required" v-slot="{ errors }">
                    <vs-input
                      :label="$t('InternationalCheckout.FirstName')"
                      :placeholder="$t('InternationalCheckout.FirstNamePlaceholder')"
                      v-model="contactForm.firstName"
                      class="w-full"
                    />
                    <span class="error-message">{{ errors[0] }}</span>
                  </ValidationProvider>
                </div>
                <div class="form-col">
                  <ValidationProvider :name="$t('InternationalCheckout.LastName')" rules="required" v-slot="{ errors }">
                    <vs-input
                      :label="$t('InternationalCheckout.LastName')"
                      :placeholder="$t('InternationalCheckout.LastNamePlaceholder')"
                      v-model="contactForm.lastName"
                      class="w-full"
                    />
                    <span class="error-message">{{ errors[0] }}</span>
                  </ValidationProvider>
                </div>
              </div>

              <div class="form-row mt-4">
                <div class="form-col">
                  <ValidationProvider :name="$t('InternationalCheckout.Email')" rules="required|email" v-slot="{ errors }">
                    <vs-input
                      :label="$t('InternationalCheckout.Email')"
                      :placeholder="$t('InternationalCheckout.EmailPlaceholder')"
                      v-model="contactForm.email"
                      class="w-full"
                    />
                    <span class="error-message">{{ errors[0] }}</span>
                  </ValidationProvider>
                </div>
                <div class="form-col">
                  <ValidationProvider :name="$t('InternationalCheckout.Phone')" rules="required" v-slot="{ errors }">
                    <vs-input
                      :label="$t('InternationalCheckout.Phone')"
                      :placeholder="$t('InternationalCheckout.PhonePlaceholder')"
                      v-model="contactForm.phone"
                      class="w-full"
                    />
                    <span class="error-message">{{ errors[0] }}</span>
                  </ValidationProvider>
                </div>
              </div>

              <!-- Billing Address Fields -->
              <h3 class="form-group-title mt-6">{{ $t('InternationalCheckout.BillingDetailsTitle') }}</h3>
              <div class="billing-type-toggle">
                <button
                  type="button"
                  :class="['toggle-btn', billingForm.type === 'individual' ? 'active' : '']"
                  @click="billingForm.type = 'individual'"
                >
                  {{ $t('InternationalCheckout.Individual') }}
                </button>
                <button
                  type="button"
                  :class="['toggle-btn', billingForm.type === 'company' ? 'active' : '']"
                  @click="billingForm.type = 'company'"
                >
                  {{ $t('InternationalCheckout.Company') }}
                </button>
              </div>

              <!-- Individual Details -->
              <div v-if="billingForm.type === 'individual'" class="mt-4">
                <ValidationProvider :name="$t('InternationalCheckout.PassportNumber')" rules="required" v-slot="{ errors }">
                  <vs-input
                    :label="$t('InternationalCheckout.PassportNumber')"
                    :placeholder="$t('InternationalCheckout.PassportNumberPlaceholder')"
                    v-model="billingForm.passportNumber"
                    class="w-full"
                  />
                  <span class="error-message">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>

              <!-- Company Details -->
              <div v-if="billingForm.type === 'company'" class="mt-4">
                <div class="form-row">
                  <div class="form-col-full">
                    <ValidationProvider :name="$t('InternationalCheckout.CompanyName')" rules="required" v-slot="{ errors }">
                      <vs-input
                        :label="$t('InternationalCheckout.CompanyName')"
                        :placeholder="$t('InternationalCheckout.CompanyNamePlaceholder')"
                        v-model="billingForm.companyName"
                        class="w-full"
                      />
                      <span class="error-message">{{ errors[0] }}</span>
                    </ValidationProvider>
                  </div>
                </div>
                <div class="form-row mt-4">
                  <div class="form-col">
                    <ValidationProvider :name="$t('InternationalCheckout.VatNumber')" rules="required" v-slot="{ errors }">
                      <vs-input
                        :label="$t('InternationalCheckout.VatNumber')"
                        :placeholder="$t('InternationalCheckout.VatNumberPlaceholder')"
                        v-model="billingForm.vatNumber"
                        class="w-full"
                      />
                      <span class="error-message">{{ errors[0] }}</span>
                    </ValidationProvider>
                  </div>
                  <div class="form-col">
                    <ValidationProvider :name="$t('InternationalCheckout.TaxOffice')" rules="required" v-slot="{ errors }">
                      <vs-input
                        :label="$t('InternationalCheckout.TaxOffice')"
                        :placeholder="$t('InternationalCheckout.TaxOfficePlaceholder')"
                        v-model="billingForm.taxOffice"
                        class="w-full"
                      />
                      <span class="error-message">{{ errors[0] }}</span>
                    </ValidationProvider>
                  </div>
                </div>
              </div>

              <!-- Common Address Fields -->
              <div class="form-row mt-4">
                <div class="form-col-full">
                  <ValidationProvider :name="$t('InternationalCheckout.StreetAddress')" rules="required" v-slot="{ errors }">
                    <vs-input
                      :label="$t('InternationalCheckout.StreetAddress')"
                      :placeholder="$t('InternationalCheckout.StreetAddressPlaceholder')"
                      v-model="billingForm.streetAddress"
                      class="w-full"
                    />
                    <span class="error-message">{{ errors[0] }}</span>
                  </ValidationProvider>
                </div>
              </div>

              <div class="form-row mt-4">
                <div class="form-col">
                  <ValidationProvider :name="$t('InternationalCheckout.CityAndCountry')" rules="required" v-slot="{ errors }">
                    <vs-input
                      :label="$t('InternationalCheckout.CityAndCountry')"
                      :placeholder="$t('InternationalCheckout.CityAndCountryPlaceholder')"
                      v-model="billingForm.cityAndCountry"
                      class="w-full"
                    />
                    <span class="error-message">{{ errors[0] }}</span>
                  </ValidationProvider>
                </div>
                <div class="form-col">
                  <ValidationProvider :name="$t('InternationalCheckout.ZipCode')" rules="required" v-slot="{ errors }">
                    <vs-input
                      :label="$t('InternationalCheckout.ZipCode')"
                      :placeholder="$t('InternationalCheckout.ZipCodePlaceholder')"
                      v-model="billingForm.zipCode"
                      class="w-full"
                    />
                    <span class="error-message">{{ errors[0] }}</span>
                  </ValidationProvider>
                </div>
              </div>

              <!-- Card Payment -->
              <h3 class="form-group-title mt-6">{{ $t('InternationalCheckout.PaymentInfoTitle') }}</h3>
              <div class="form-row">
                <div class="form-col-full">
                  <ValidationProvider :name="$t('Card.Name')" rules="required" v-slot="{ errors }">
                    <vs-input
                      :label="$t('Card.Name')"
                      :placeholder="$t('Card.Name')"
                      v-model="formData.cardName"
                      @input="changeName"
                      class="w-full"
                    />
                    <span class="error-message">{{ errors[0] }}</span>
                  </ValidationProvider>
                </div>
              </div>

              <div class="form-row mt-4">
                <div class="form-col-full">
                  <ValidationProvider :name="$t('Card.Number')" rules="required|ccnumber" v-slot="{ errors }">
                    <vs-input
                      :label="$t('Card.Number')"
                      :placeholder="$t('Card.Number')"
                      v-model="formData.cardNumber"
                      @input="changeNumber"
                      :maxlength="cardNumberMaxLength"
                      class="w-full"
                    />
                    <span class="error-message">{{ errors[0] }}</span>
                  </ValidationProvider>
                </div>
              </div>

              <div class="form-row mt-4">
                <div class="form-col">
                  <label class="input-select-label">{{ $t('Card.Month') }}</label>
                  <v-select
                    :options="monthOptions"
                    v-model="formData.cardMonth"
                    :placeholder="$t('Card.Month')"
                    :clearable="false"
                    class="w-full select-field"
                  />
                </div>
                <div class="form-col">
                  <label class="input-select-label">{{ $t('Card.Year') }}</label>
                  <v-select
                    :options="yearOptions"
                    v-model="formData.cardYear"
                    :placeholder="$t('Card.Year')"
                    :clearable="false"
                    class="w-full select-field"
                  />
                </div>
                <div class="form-col">
                  <ValidationProvider :name="$t('Card.CVV')" rules="required|min:3|max:4" v-slot="{ errors }">
                    <vs-input
                      :label="$t('Card.CVV')"
                      placeholder="123"
                      v-model="formData.cardCvv"
                      @input="changeCvv"
                      maxlength="4"
                      class="w-full"
                    />
                    <span class="error-message">{{ errors[0] }}</span>
                  </ValidationProvider>
                </div>
              </div>
            </form>
          </ValidationObserver>
        </div>

        <!-- Right Column: Card Graphic & Invoice Summary -->
        <div class="checkout-summary-section">
          <!-- Card Graphic -->
          <div class="card-graphic-container">
            <Card
              :fields="fields"
              :labels="formData"
              :isCardNumberMasked="isCardNumberMasked"
              :randomBackgrounds="false"
            />
          </div>

          <!-- Order Summary Details -->
          <div class="summary-details">
            <h3 class="summary-title">{{ $t('InternationalCheckout.PaymentSummary') }}</h3>
            <div class="summary-divider"></div>
            <div class="summary-row">
              <span class="summary-label">{{ $t('InternationalCheckout.ProgramDesc') }}</span>
              <span class="summary-value text-right">{{ invoiceData.SettingGroup.ProductDesc || invoiceData.SettingGroup.ProductTitle }}</span>
            </div>
            <div class="summary-divider"></div>
            <div class="summary-row total-row">
              <span class="total-label">{{ $t('InternationalCheckout.TotalAmount') }}</span>
              <span class="total-value">{{ formattedPrice }}</span>
            </div>
          </div>

          <!-- Pay Button -->
          <div class="mt-8">
            <vs-button
              class="w-full pay-submit-btn"
              color="success"
              type="filled"
              @click="processCheckout"
              :disabled="submitBusy"
            >
              {{ submitBusy ? $t('InternationalCheckout.Processing') : $t('InternationalCheckout.PayNow') }}
            </vs-button>
          </div>

          <!-- Security Badges -->
          <div class="secure-badges">
            <span class="badge-item">
              <i class="material-icons badge-icon">lock</i>
              {{ $t('InternationalCheckout.SecurePayments') }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Hidden redirection forms for 3D Secure bank interface -->
    <form ref="threeDRedirectForm" method="post" :action="threeDInfo.AcsUrl">
      <input type="hidden" name="PaReq" :value="threeDInfo.PAReq" />
      <input type="hidden" name="TermUrl" :value="threeDInfo.TermUrl" />
      <input type="hidden" name="MD" :value="threeDInfo.MD" />
    </form>
    <form ref="threeDRedirectFormHidden" method="post" :action="threeDInfo.AcsUrl">
      <input v-for="(val, key) in threeDInfo.Model" :key="key" type="hidden" :name="key" :value="val" />
    </form>
  </div>
</template>

<script>
import Vue from "vue";
import InvoiceApi from "@/api/invoice";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import Card from "@/components/Card.vue";

var cardValid = require("card-validator");

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    vSelect,
    Card
  },
  data() {
    return {
      loading: true,
      not_found: false,
      submitBusy: false,
      invoiceData: null,
      invoiceId: this.$route.params.invoiceId,
      isCardNumberMasked: false,
      cardNumberMaxLength: 19,

      contactForm: {
        firstName: "",
        lastName: "",
        email: "",
        phone: ""
      },

      billingForm: {
        type: "individual",
        passportNumber: "",
        companyName: "",
        vatNumber: "",
        taxOffice: "",
        streetAddress: "",
        cityAndCountry: "",
        zipCode: ""
      },

      formData: {
        cardName: "",
        cardNumber: "",
        cardMonth: { label: "", value: "" },
        cardYear: { label: "", value: "" },
        cardCvv: ""
      },

      fields: {
        cardNumber: "v-card-number",
        cardName: "v-card-name",
        cardMonth: "v-card-month",
        cardYear: "v-card-year",
        cardCvv: "v-card-cvv"
      },

      threeDInfo: {
        AcsUrl: "",
        PAReq: "",
        TermUrl: "",
        MD: "",
        Model: {}
      }
    };
  },
  watch: {
    "$i18n.locale"() {
      this.updateDateLabels();
    }
  },
  computed: {
    monthOptions() {
      return [
        { label: "01 - " + this.$t("Months.Jan"), value: "01" },
        { label: "02 - " + this.$t("Months.Feb"), value: "02" },
        { label: "03 - " + this.$t("Months.Mar"), value: "03" },
        { label: "04 - " + this.$t("Months.Apr"), value: "04" },
        { label: "05 - " + this.$t("Months.May"), value: "05" },
        { label: "06 - " + this.$t("Months.Jun"), value: "06" },
        { label: "07 - " + this.$t("Months.Jul"), value: "07" },
        { label: "08 - " + this.$t("Months.Aug"), value: "08" },
        { label: "09 - " + this.$t("Months.Sep"), value: "09" },
        { label: "10 - " + this.$t("Months.Oct"), value: "10" },
        { label: "11 - " + this.$t("Months.Nov"), value: "11" },
        { label: "12 - " + this.$t("Months.Dec"), value: "12" }
      ];
    },
    yearOptions() {
      const currentYear = new Date().getFullYear();
      let years = [];
      for (let i = 0; i <= 15; i++) {
        const yr = currentYear + i;
        years.push({
          label: yr.toString(),
          value: yr.toString()
        });
      }
      return years;
    },
    formattedPrice() {
      if (!this.invoiceData || !this.invoiceData.SettingGroup) return "";
      const amount = this.invoiceData.IsPaymentRequired.Amount || 0;
      const key = this.invoiceData.SettingGroup.InvoiceSettingKey || "";
      let symbol = "$";
      if (key.toUpperCase().includes("EUR")) symbol = "€";
      else if (key.toUpperCase().includes("GBP")) symbol = "£";

      return symbol + " " + Number(amount).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
  },
  methods: {
    updateDateLabels() {
      if (!this.formData.cardMonth || !this.formData.cardMonth.value) {
        this.formData.cardMonth = { label: this.$t("Card.Month"), value: "" };
      }
      if (!this.formData.cardYear || !this.formData.cardYear.value) {
        this.formData.cardYear = { label: this.$t("Card.Year"), value: "" };
      }
    },
    async fetchInvoiceData() {
      this.loading = true;
      try {
        let result = await InvoiceApi.getInvoice({ InvoiceKey: this.invoiceId }, this);
        if (!result.data.error && result.data.Invoice) {
          this.invoiceData = result.data;
          
          // Check if invoice has been paid
          if (!result.data.IsPaymentRequired.Need) {
            this.$vs.notify({
              time: 6000,
              title: this.$t("InternationalCheckout.NotRequiredTitle"),
              text: this.$t("InternationalCheckout.NotRequiredDesc"),
              iconPack: "feather",
              icon: "icon-info",
              color: "primary",
              position: "top-right"
            });
          }
        } else {
          this.not_found = true;
        }
      } catch (err) {
        this.not_found = true;
      } finally {
        this.loading = false;
      }
    },

    changeName() {
      this.$emit("input-card-name", this.formData.cardName);
    },

    changeNumber() {
      let value = this.formData.cardNumber.replace(/\D/g, "");
      if (/^3[47]\d{0,13}$/.test(value)) {
        this.formData.cardNumber = value
          .replace(/(\d{4})/, "$1 ")
          .replace(/(\d{4}) (\d{6})/, "$1 $2 ");
        this.cardNumberMaxLength = 17;
      } else if (/^3(?:0[0-5]|[68]\d)\d{0,11}$/.test(value)) {
        this.formData.cardNumber = value
          .replace(/(\d{4})/, "$1 ")
          .replace(/(\d{4}) (\d{6})/, "$1 $2 ");
        this.cardNumberMaxLength = 16;
      } else {
        this.formData.cardNumber = value
          .replace(/(\d{4})/, "$1 ")
          .replace(/(\d{4}) (\d{4})/, "$1 $2 ")
          .replace(/(\d{4}) (\d{4}) (\d{4})/, "$1 $2 $3 ");
        this.cardNumberMaxLength = 19;
      }
      this.$emit("input-card-number", this.formData.cardNumber);
    },

    changeCvv() {
      this.$emit("input-card-cvv", this.formData.cardCvv);
    },

    async processCheckout() {
      const isValid = await this.$refs.checkoutForm.validate();
      if (!isValid) {
        this.$vs.notify({
          time: 3000,
          title: this.$t("InternationalCheckout.ValidationError"),
          text: this.$t("InternationalCheckout.ValidationDesc"),
          iconPack: "feather",
          icon: "icon-alert-circle",
          color: "warning",
          position: "top-right"
        });
        return;
      }

      if (!this.formData.cardMonth || !this.formData.cardMonth.value || !this.formData.cardYear || !this.formData.cardYear.value) {
        this.$vs.notify({
          time: 3000,
          title: this.$t("InternationalCheckout.ExpiryRequired"),
          text: this.$t("InternationalCheckout.ExpiryRequiredDesc"),
          iconPack: "feather",
          icon: "icon-alert-circle",
          color: "warning",
          position: "top-right"
        });
        return;
      }

      var expiryValidation = cardValid.expirationDate({
        month: this.formData.cardMonth.value,
        year: this.formData.cardYear.value
      });

      if (!expiryValidation.isPotentiallyValid) {
        this.$vs.notify({
          time: 3000,
          title: this.$t("InternationalCheckout.ExpiryInvalid"),
          text: this.$t("InternationalCheckout.ExpiryInvalidDesc"),
          iconPack: "feather",
          icon: "icon-alert-circle",
          color: "danger",
          position: "top-right"
        });
        return;
      }

      this.submitBusy = true;
      this.$vs.loading({
        color: "#000",
        container: ".pay-submit-btn",
        scale: 0.45
      });

      try {
        // Step 1: Register guest user via setUser
        let setUserData = {
          InvoiceKey: this.invoiceId.toString(),
          LocalUser: false,
          IdentityNumber: this.billingForm.type === "individual" 
            ? this.billingForm.passportNumber.trim() 
            : this.billingForm.vatNumber.trim(),
          FullName: this.contactForm.firstName.trim() + " " + this.contactForm.lastName.trim(),
          Email: this.contactForm.email.trim(),
          Phone: this.contactForm.phone.trim()
        };

        let setUserResult = await InvoiceApi.setUser(setUserData, this);
        
        if (setUserResult.data.error || setUserResult.data.errors) {
          throw new Error(setUserResult.data.error || this.$t("InternationalCheckout.StudentRegFailed"));
        }

        // Step 2: Establish local user session
        this.$auth.logOut();
        this.$auth.localLogin(setUserResult.data);

        // Step 3: Save billing details
        let addressData = {
          InvoiceId: this.invoiceData.Invoice.InvoiceID,
          UserAddressType: this.billingForm.type === "individual" ? 0 : 1,
          IdentityNumber: this.billingForm.type === "individual" 
            ? this.billingForm.passportNumber.trim() 
            : this.billingForm.vatNumber.trim(),
          Address: this.billingForm.streetAddress.trim() + ", " + this.billingForm.cityAndCountry.trim(),
          CompanyName: this.billingForm.type === "company" ? this.billingForm.companyName.trim() : "",
          TaxAdmin: this.billingForm.type === "company" ? this.billingForm.taxOffice.trim() : "",
          TaxNumber: this.billingForm.type === "company" ? this.billingForm.vatNumber.trim() : "",
          TaxCode: this.billingForm.zipCode.trim(),
          EInvoice: false,
          DistrictId: null
        };

        let addressResult = await InvoiceApi.saveUserAddress(addressData, this);
        if (addressResult.data.error) {
          throw new Error(addressResult.data.error || this.$t("InternationalCheckout.BillingRegFailed"));
        }

        // Step 4: Call doPayment to initiate VPOS payment
        let payFormData = {
          Paymethod: "VakifbankClassic",
          InvoiceKey: this.invoiceId.toString(),
          CardOwner: this.formData.cardName.trim().toUpperCase(),
          CardNumber: this.formData.cardNumber.replace(/\D/g, ""),
          CardMonth: this.formData.cardMonth.value.toString(),
          CardYear: this.formData.cardYear.value.toString(),
          CardCvv: this.formData.cardCvv.toString(),
          Installment: "1"
        };

        let payResult = await InvoiceApi.doPayment(payFormData, this);
        
        if (payResult.data.error || payResult.data.errors) {
          throw new Error(payResult.data.error || this.$t("InternationalCheckout.PaymentDeclined"));
        }

        // Handle 3D Secure Redirection
        if (!payResult.data.HiddenForm) {
          this.threeDInfo.AcsUrl = payResult.data.AcsUrl;
          this.threeDInfo.PAReq = payResult.data.PAReq;
          this.threeDInfo.TermUrl = payResult.data.TermUrl;
          this.threeDInfo.MD = payResult.data.MD;

          setTimeout(() => {
            this.$refs.threeDRedirectForm.submit();
          }, 100);
        } else {
          this.threeDInfo.AcsUrl = payResult.data.AcsUrl;
          this.threeDInfo.Model = payResult.data.Model;

          setTimeout(() => {
            this.$refs.threeDRedirectFormHidden.submit();
          }, 100);
        }
      } catch (err) {
        this.$vs.notify({
          time: 5000,
          title: this.$t("InternationalCheckout.PaymentError"),
          text: err.message || this.$t("InternationalCheckout.UnexpectedError"),
          iconPack: "feather",
          icon: "icon-alert-circle",
          color: "danger",
          position: "top-right"
        });
      } finally {
        this.submitBusy = false;
        this.$vs.loading.close(".pay-submit-btn");
      }
    }
  },
  created() {
    this.updateDateLabels();
  },
  mounted() {
    this.fetchInvoiceData();
  }
};
</script>

<style scoped>
.international-checkout-container {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: radial-gradient(circle at 10% 20%, rgba(98, 114, 240, 0.05) 0%, rgba(223, 227, 255, 0.05) 90.1%);
  padding: 2rem 1rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.checkout-card {
  width: 100%;
  max-width: 1200px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(226, 232, 240, 0.8);
  border-radius: 20px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 10px 10px -5px rgba(0, 0, 0, 0.02);
  backdrop-filter: blur(10px);
  overflow: visible;
  padding: 2.5rem;
}

.checkout-loading-wrapper, .checkout-error-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
}

.spinner {
  border: 4px solid rgba(98, 114, 240, 0.1);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border-left-color: #6272f0;
  animation: spin 1s linear infinite;
  margin-bottom: 1.5rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.checkout-grid {
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  gap: 2rem;
}

@media (max-width: 900px) {
  .checkout-grid {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  .checkout-card {
    padding: 1.5rem;
  }
}

.section-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.section-subtitle {
  font-size: 0.95rem;
  color: #64748b;
  margin-bottom: 2rem;
}

.form-group-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f1f5f9;
}

.form-row {
  display: flex;
  gap: 1rem;
}

.form-col {
  flex: 1;
  min-width: 0;
}

.form-col-full {
  width: 100%;
}

.error-message {
  display: block;
  font-size: 0.8rem;
  color: #ef4444;
  margin-top: 0.25rem;
}

.billing-type-toggle {
  display: flex;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 10px;
  margin-bottom: 1.5rem;
  width: fit-content;
}

.toggle-btn {
  border: none;
  background: transparent;
  padding: 0.5rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.toggle-btn.active {
  background: #fff;
  color: #6272f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.input-select-label {
  display: block;
  font-size: 0.85rem;
  color: #64748b;
  margin-bottom: 0.4rem;
  font-weight: 500;
}

.select-field >>> .vs__dropdown-toggle {
  border: 1px solid rgba(0,0,0,0.2) !important;
  border-radius: 5px;
  padding: 3px 0;
  background: #fff;
}

.checkout-summary-section {
  background: #f8fafc;
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  border: 1px solid #f1f5f9;
}

.card-graphic-container {
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
}

.summary-details {
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid #e2e8f0;
}

.summary-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 1rem;
}

.summary-divider {
  height: 1px;
  background: #f1f5f9;
  margin: 0.75rem 0;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-label {
  font-size: 0.85rem;
  color: #64748b;
}

.summary-value {
  font-size: 0.9rem;
  font-weight: 600;
  color: #334155;
}

.total-row {
  margin-top: 0.5rem;
}

.total-label {
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
}

.total-value {
  font-size: 1.35rem;
  font-weight: 800;
  color: #10b981;
}

.pay-submit-btn {
  font-size: 1.05rem !important;
  font-weight: 600 !important;
  padding: 0.8rem 2rem !important;
  border-radius: 10px !important;
  box-shadow: 0 10px 15px -3px rgba(16, 185, 129, 0.2) !important;
  transition: all 0.2s ease !important;
}

.pay-submit-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 20px -3px rgba(16, 185, 129, 0.3) !important;
}

.secure-badges {
  margin-top: 1.5rem;
  display: flex;
  justify-content: center;
}

.badge-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: #94a3b8;
  font-weight: 500;
}

.badge-icon {
  font-size: 1rem;
}
</style>
